import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    IconButton,
    Tooltip,
    Typography
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import PersonIcon from "@mui/icons-material/Person";
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { asyncToggleTourLike } from '../../../store/Vanlifer/tourSlice';
import { useSnackbar } from '../../SnackbarProvider';
import { TourOverview } from '../../../services/restserver-openapi';
import { formatAsSwissDate } from '../../../utils/dateUtil';
import imgSlider1 from "../../../assets/Images/trip_planner.jpg";

interface TourCardPublicProps {
    tour: TourOverview;
    showOwner: boolean;
    isOwner: boolean;
}

export default function TourCardPublic({ tour, showOwner, isOwner }: TourCardPublicProps) {
    const dispatch = useAppDispatch();
    const { showSnackbar } = useSnackbar();
    const isAuthenticated = useAppSelector(state => state.login.isAuthenticated);

    const [liked, setLiked] = useState(tour.liked || false);
    const [likeCount, setLikeCount] = useState(tour.likeCount || 0);

    useEffect(() => {
        setLiked(tour.liked || false);
        setLikeCount(tour.likeCount || 0);
    }, [tour.liked, tour.likeCount]);

    const { fromDate, toDate } = tour.stays?.reduce(
        (acc, stay) => ({
            fromDate: Math.min(acc.fromDate ?? stay.fromDate ?? Infinity, stay.fromDate ?? Infinity),
            toDate: Math.max(acc.toDate ?? stay.toDate ?? 0, stay.toDate ?? 0),
        }),
        {} as { fromDate?: number; toDate?: number }
    ) || {};

    const rightImage =
        tour.sasDownloadLink !== '' && tour.sasDownloadLink != null
            ? tour.sasDownloadLink
            : imgSlider1.toString();

    const handleLike = useCallback(async () => {
        try {
            await dispatch(
                asyncToggleTourLike({ tourId: tour.tourId!, isLiked: !liked })
            ).unwrap();
        } catch (e) {
            showSnackbar('Liken fehlgeschlagen – bitte versuche es erneut', 'error');
        }
    }, [dispatch, tour.tourId, liked, showSnackbar]);

    return (
        <Card
            sx={{
                position: 'relative',
                height: 320,
                color: 'white',
                textDecoration: 'none',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                borderRadius: 2,
                '&:hover img': {
                    transform: 'scale(1.05)',
                },
            }}
            elevation={3}
        >
            {/* Like button — outside the link */}
            {isAuthenticated && tour.sharingMode !== 'PRIVATE' && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 2,
                        mt: 0.3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Tooltip title={liked ? 'Like entfernen' : 'Like hinzufügen'}>
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                handleLike();
                            }}
                            sx={{
                                color: liked ? 'red' : 'white',
                            }}
                        >
                            {liked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                        </IconButton>
                    </Tooltip>
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'white',
                            fontSize: '0.65rem',
                            mt: 0.25,
                            lineHeight: 1,
                        }}
                    >
                        {likeCount}
                    </Typography>
                </Box>
            )}

            {/* Tour link content */}
            <Box
                component={RouterLink}
                to={`/vanlifer/Community/Tour/${tour.tourId}`}
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    textDecoration: 'none',
                    color: 'inherit',
                }}
            >
                <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'hidden', zIndex: 0 }}>
                    <Box
                        component="img"
                        src={rightImage}
                        alt="Tour background"
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s ease' }}
                    />
                </Box>

                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.22) 40%, rgba(0,0,0,0.15) 50%, rgba(0,0,0,0.22) 60%, rgba(0,0,0,0.55) 100%)',
                        zIndex: 1,
                    }}
                />

                <CardContent
                    sx={{
                        position: 'relative',
                        zIndex: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        height: '100%',
                        width: '100%',
                        p: 2,
                    }}
                >
                    <Box>
                        <Typography variant="h5" sx={{ mb: 0.5, pr: 3 }}>
                            {tour.name || 'Ohne Name'}
                        </Typography>
                        <Typography variant="caption">
                            {fromDate && toDate
                                ? `${formatAsSwissDate(fromDate)} – ${formatAsSwissDate(toDate)}`
                                : 'Reisedaten unbekannt'}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            {showOwner && (
                                <>
                                    <Typography variant="caption">{tour.owner ?? "Unbekannter User"}</Typography>
                                    {isOwner && (
                                        <Tooltip title="Deine Story">
                                            <PersonIcon fontSize="inherit" />
                                        </Tooltip>
                                    )}
                                </>
                            )}
                        </Box>
                    </Box>

                    <Typography
                        variant="body2"
                        sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'normal',
                            mt: 1,
                        }}
                    >
                        {tour.description}
                    </Typography>
                </CardContent>
            </Box>
        </Card>
    );
}